import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import SupplierEconomicsPanel from "@/components/suppliers/SupplierEconomicsPanel";
import SupplierConditionsModal from "@/components/suppliers/SupplierConditionsModal";
import MoleculeLabMatrixModal from "@/components/suppliers/MoleculeLabMatrixModal";
import LabDuplicatesModal from "@/components/suppliers/LabDuplicatesModal";
import MultiLabImportModal from "@/components/suppliers/MultiLabImportModal";
import BrandDiscountsModal from "@/components/suppliers/BrandDiscountsModal";
import BestSupplierSimulationModal from "@/components/suppliers/BestSupplierSimulationModal";
import LabComparisonModal from "@/components/suppliers/LabComparisonModal";
import Top25BestOptionModal from "@/components/top25/Top25BestOptionModal";
import SupplierHealthDashboard from "@/components/suppliers/SupplierHealthDashboard";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadXlsx } from "@/lib/download";
import { ArrowLeft, Handshake, Loader2, FileSpreadsheet, Grid3x3, Merge, UploadCloud, Sparkles, Trash2, Star, ArrowRightLeft, Trophy } from "lucide-react";

/**
 * Acceso directo a las condiciones económicas de los laboratorios sin tener
 * que entrar al detalle del estudio. Usa el estudio vigente como base.
 */
export default function SupplierConditionsHubPage() {
  const { current, loading } = useCurrentStudy();
  const [condModalLab, setCondModalLab] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showDupModal, setShowDupModal] = useState(false);
  const [showMultiImport, setShowMultiImport] = useState(false);
  const [showBrandDiscounts, setShowBrandDiscounts] = useState(false);
  const [showBestSupplier, setShowBestSupplier] = useState(false);
  const [showLabComparison, setShowLabComparison] = useState(false);
  const [showTop25Best, setShowTop25Best] = useState(false);
  const [legacyShorts, setLegacyShorts] = useState(null); // {items:[], count:number}
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (!current?.id) return;
    let alive = true;
    api.get("/supplier-agreements/legacy-shorts/preview").then(({ data }) => {
      if (alive) setLegacyShorts(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [current?.id, refreshKey]);

  const purgeLegacyShorts = async () => {
    if (!legacyShorts?.count) return;
    const labs = legacyShorts.items.map(i => i.laboratorio).join(", ");
    if (!window.confirm(
      `Se van a borrar ${legacyShorts.count} convenio(s) con nombre corto que colisionan con los labs oficiales del catálogo:\n\n${labs}\n\n` +
      "Después podrás re-importar tu Excel multi-lab y cada pestaña se guardará bajo el nombre canónico correcto. ¿Continuar?"
    )) return;
    setPurging(true);
    try {
      const { data } = await api.post("/supplier-agreements/legacy-shorts/purge");
      toast.success(`${data.deleted} convenio(s) borrado(s). Ahora re-importa tu Excel multi-lab.`);
      setLegacyShorts({ items: [], count: 0 });
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al limpiar");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1600px]" data-testid="supplier-conditions-page">
      <Link
        to="/proveedores"
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6"
        data-testid="back-to-proveedores"
      >
        <ArrowLeft className="h-4 w-4" /> Proveedores
      </Link>

      <div className="mb-8 grid lg:grid-cols-[1fr_minmax(0,520px)] gap-6 items-center">
        <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-2">
          <Handshake className="h-3 w-3" /> Condiciones comerciales
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">
          Condiciones de laboratorios
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Configura el convenio, descuento medio y vademécum de cada laboratorio.
          Pulsa <strong>&ldquo;Condiciones&rdquo;</strong> en un laboratorio para editarlo (también puedes
          importar la oferta desde un PDF o imagen con IA).
        </p>
        </div>
        <img
          src="/condiciones-laboratorio.jpg"
          alt="FarmaClass · Condiciones de laboratorio: convenios, descuentos y vademécum"
          className="w-full rounded-2xl shadow-lg ring-1 ring-slate-900/10"
          data-testid="supplier-conditions-banner"
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 py-12">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando estudio vigente…
        </div>
      )}

      {!loading && !current?.id && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="no-study-hint">
          No hay ningún estudio cargado. Sube una dispensación para analizar tus proveedores
          y configurar sus condiciones.
          <div className="mt-4">
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg"
            >
              Subir dispensación →
            </Link>
          </div>
        </div>
      )}

      {!loading && current?.id && (
        <>
          <SupplierHealthDashboard dispId={current.id} refreshKey={refreshKey} />

          {legacyShorts?.count > 0 && (
            <div className="mb-4 bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="legacy-shorts-banner">
              <Trash2 className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-amber-900">
                  Hay {legacyShorts.count} convenio(s) con nombre corto que colisionan con el catálogo
                </div>
                <div className="text-xs text-amber-800 mt-1">
                  {legacyShorts.items.map((it) => (
                    <span key={it.laboratorio}>
                      <strong>{it.laboratorio}</strong> → {it.canonical_match}
                      {" · "}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-amber-700 mt-2">
                  Estos convenios vienen del multi-import antiguo. Bórralos y vuelve a subir tu Excel: cada pestaña quedará bajo el nombre canónico correcto.
                </div>
              </div>
              <button
                onClick={purgeLegacyShorts}
                disabled={purging}
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-bold px-3.5 py-2 rounded-lg flex-shrink-0"
                data-testid="purge-legacy-shorts-btn"
              >
                {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Limpiar y re-importar
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-xs text-slate-500">
              Estudio base: <strong className="text-slate-800">{current.label}</strong>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowMultiImport(true)}
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="open-multi-lab-import"
                title="Sube un Excel con una pestaña por laboratorio (KERN, SANDOZ, TEVA…). Cada pestaña se importa automáticamente como una tarifa distinta."
              >
                <UploadCloud className="h-4 w-4" /> Importar tarifas multi-lab
              </button>
              <button
                onClick={() => setShowBrandDiscounts(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="open-brand-discounts"
                title="Selecciona los laboratorios de marca con los que has negociado descuento adicional y establece el %. FarmaClass calcula el beneficio real."
              >
                <Star className="h-4 w-4" /> Dtos de labs de marca
              </button>
              <button
                onClick={() => setShowBestSupplier(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="best-supplier-simulation"
                title="Abre la pantalla con la comparativa lab actual vs mejor lab por molécula, ordenada por ahorro €/mes. Descarga Excel o PDF."
              >
                <Sparkles className="h-4 w-4" /> Opción más rentable
              </button>
              <button
                onClick={() => setShowTop25Best(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="open-top25-best-option"
                title="Top 25 moléculas más dispensadas (o por € de marca/genérico) con el mejor laboratorio entre tus condiciones y el ahorro €/mes. Incluye con qué labs te falta condición."
              >
                <Trophy className="h-4 w-4" /> Top 25 para ahorrar
              </button>
              <button
                onClick={() => setShowLabComparison(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="open-lab-comparison"
                title="Elige dos laboratorios y compara descuentos, moléculas cubiertas y ahorro potencial."
              >
                <ArrowRightLeft className="h-4 w-4" /> Comparar 2 labs
              </button>
              <button
                onClick={() => setShowDupModal(true)}
                className="inline-flex items-center gap-2 bg-white border-2 border-amber-400 hover:bg-amber-50 text-amber-700 text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="merge-lab-duplicates"
                title={"Revisa uno a uno los laboratorios con variantes de nombre (ej: con y sin coma) y elige cuáles unificar"}
              >
                <Merge className="h-4 w-4" /> Unificar duplicados
              </button>
              <button
                onClick={() => setShowMatrix(true)}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="open-molecule-lab-matrix"
                title="Vista global: qué laboratorio cubre cada molécula, con qué descuento, y cuánto puedes ahorrar"
              >
                <Grid3x3 className="h-4 w-4" /> Matriz molécula × lab
              </button>
              <button
                onClick={() => downloadXlsx(`/dispensations/${current.id}/purchases-export`, "compras_mensuales.xlsx")}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
                data-testid="purchases-export-excel"
                title="Excel con volumen de compras mensuales: resumen, una hoja por laboratorio y desglose por molécula"
              >
                <FileSpreadsheet className="h-4 w-4" /> Excel compras mensuales
              </button>
            </div>
          </div>
          <SupplierEconomicsPanel
            dispId={current.id}
            refreshKey={refreshKey}
            onConfigure={(lab) => setCondModalLab(lab)}
          />
          {condModalLab && (
            <SupplierConditionsModal
              laboratorio={condModalLab}
              onClose={() => setCondModalLab(null)}
              onSaved={() => setRefreshKey((k) => k + 1)}
            />
          )}
          {showMatrix && (
            <MoleculeLabMatrixModal
              dispId={current.id}
              onClose={() => setShowMatrix(false)}
            />
          )}
          {showDupModal && (
            <LabDuplicatesModal
              onClose={() => setShowDupModal(false)}
              onMerged={() => { setShowDupModal(false); setRefreshKey((k) => k + 1); }}
            />
          )}
          {showMultiImport && (
            <MultiLabImportModal
              onClose={() => setShowMultiImport(false)}
              onApplied={() => { setShowMultiImport(false); setRefreshKey((k) => k + 1); }}
            />
          )}
          {showBrandDiscounts && (
            <BrandDiscountsModal
              dispId={current.id}
              onClose={() => setShowBrandDiscounts(false)}
              onSaved={() => setRefreshKey((k) => k + 1)}
            />
          )}
          {showBestSupplier && (
            <BestSupplierSimulationModal
              dispId={current.id}
              onClose={() => setShowBestSupplier(false)}
            />
          )}
          {showTop25Best && (
        <Top25BestOptionModal
          dispensationId={current.id}
          label={current.label}
          initialBasis="unidades"
          onClose={() => setShowTop25Best(false)}
        />
      )}

      {showLabComparison && (
            <LabComparisonModal
              dispId={current.id}
              onClose={() => setShowLabComparison(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
